import React, { useCallback, useState } from "react";
import { commitMutation } from "react-relay";

import { ConnectionHandler, graphql } from "relay-runtime";

import type { GroupType } from "~/actionItems/types";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal, TextField } from "~/ui/legacyFellowIndex";
import toasty from "~/ui/toast";

import type { CreateOrEditActionItemGroupCreateMutation } from "~/__generated__/CreateOrEditActionItemGroupCreateMutation.graphql";
import type { CreateOrEditActionItemGroupEditMutation } from "~/__generated__/CreateOrEditActionItemGroupEditMutation.graphql";

import styles from "./CreateOrEditActionItemGroup.module.css";

const createActionItemGroupMutation = graphql`
	mutation CreateOrEditActionItemGroupCreateMutation($groupName: String!, $order: Int!) {
		createCustomActionItemGroup(groupName: $groupName, order: $order) {
			customGroup {
				id
				name
				order
				isDefaultGroup
				items {
					edges {
						node {
							...ActionItemData @relay(mask: false)
						}
					}
				}
			}
		}
	}
`;

const editActionItemGroupMutation = graphql`
	mutation CreateOrEditActionItemGroupEditMutation($groupId: ID!, $newName: String!) {
		editCustomActionItemGroup(groupId: $groupId, newName: $newName) {
			editedGroup {
				id
				name
			}
		}
	}
`;

interface CreateActionItemGroupProps {
	group?: GroupType;
	order?: number;
	onClose(): void;
	itemsKey: string;
}

function createEmptyGroup(order?: number) {
	return { id: "", name: "", order: order || 0, isDefaultGroup: false, items: { edges: [] } };
}
const GROUP_NAME_MAX_LEN = 120;

export default function CreateOrEditActionItemGroup({
	group: editGroup,
	order,
	onClose,
	itemsKey,
}: CreateActionItemGroupProps) {
	const [group, setGroup] = useState<GroupType>(editGroup || createEmptyGroup(order));
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onChangeName = useCallback((newName: string) => {
		setGroup(group => ({ ...group, name: newName }));
	}, []);

	const onEdit = useCallback(() => {
		setIsLoading(true);
		commitMutation<CreateOrEditActionItemGroupEditMutation>(environment, {
			mutation: editActionItemGroupMutation,
			variables: { groupId: group.id, newName: group.name },
			onCompleted: (response, error) => {
				setIsLoading(false);
				if (error) {
					toasty.error("Failed to edit group.");
					console.error(error);
				}
				onClose();
			},
		});
	}, [group, onClose]);
	const onCreate = useCallback(() => {
		setIsLoading(true);
		commitMutation<CreateOrEditActionItemGroupCreateMutation>(environment, {
			mutation: createActionItemGroupMutation,
			variables: { groupName: group.name, order: group.order },
			onCompleted: (response, error) => {
				setIsLoading(false);
				if (error) {
					toasty.error("Failed to create new group.");
					console.error(error);
				}
				onClose();
			},
			updater: (store, response) => {
				if (!response) return;
				if (!response.createCustomActionItemGroup?.customGroup) {
					return;
				}
				const rootRecord = store.getRoot();
				if (!rootRecord) {
					return;
				}
				const connectionRecord = ConnectionHandler.getConnection(rootRecord, "ActionItemGroupsList_actionItemGroups");
				if (!connectionRecord) {
					return;
				}
				const customGroupRecord = store.get(response.createCustomActionItemGroup.customGroup.id);
				if (!customGroupRecord) {
					return;
				}
				// When you have parameters that are used as part of a field, simply adding the new item to the connection
				// will not work, because the new relay record doesnt have the parameters on that field.
				// I.e. the "items" field will be stored on the real records with all the parameters stringified in alphabetical
				// order.
				// So we need to manually relink the "items" field with the correct parameters on the new group record
				const createdGroupItems = customGroupRecord.getLinkedRecord("items");
				customGroupRecord.setLinkedRecord(createdGroupItems, itemsKey);

				const edge = ConnectionHandler.createEdge(store, connectionRecord, customGroupRecord, "ActionItemGroupNode");
				ConnectionHandler.insertEdgeBefore(connectionRecord, edge);
			},
		});
	}, [group, onClose, itemsKey]);

	const onCancel = useCallback(() => {
		onClose();
	}, [onClose]);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				editGroup ? onEdit() : onCreate();
			}
		},
		[editGroup, onEdit, onCreate],
	);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>{editGroup ? "Rename custom group" : "Create custom group"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<label className={styles.label}>
					Group name
					<span>
						{group.name.length}/{GROUP_NAME_MAX_LEN}
					</span>
				</label>
				<TextField
					className={styles.input}
					value={group.name}
					setValue={onChangeName}
					onKeyDown={onKeyDown}
					placeholder="Ex. High priority"
					maxLength={GROUP_NAME_MAX_LEN}
					autoFocus
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onCancel} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					variant="primary"
					size="normal"
					onClick={editGroup ? onEdit : onCreate}
					disabled={!group.name || isLoading}
				>
					{editGroup ? "Save" : "Create"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
